import { ref, reactive, computed, watch, onScopeDispose } from 'vue';
import client from '@website/http/client.js';
import debounce from '@app/utils/debounce.js';

const URL_FILTER_KEYS = [
  'keyword', 'type', 'category_id', 'zone_id',
  'price_min', 'price_max', 'bedrooms_min', 'bathrooms_min', 'parking_min',
];

const NUMERIC_KEYS = new Set([
  'category_id', 'zone_id', 'price_min', 'price_max',
  'bedrooms_min', 'bathrooms_min', 'parking_min',
]);

function readFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const result = {};
  for (const key of URL_FILTER_KEYS) {
    const val = params.get(key);
    if (val != null && val !== '') {
      result[key] = NUMERIC_KEYS.has(key) ? Number(val) : val;
    }
  }
  return result;
}

function writeFiltersToUrl(filters) {
  const params = new URLSearchParams(window.location.search);
  for (const key of URL_FILTER_KEYS) params.delete(key);
  for (const key of URL_FILTER_KEYS) {
    const val = filters[key];
    if (val != null && val !== '') params.set(key, String(val));
  }
  const qs = params.toString();
  const url = window.location.pathname + (qs ? `?${qs}` : '');
  history.replaceState(null, '', url);
}

export function usePropertySearch(searchUrl, { initialPerPage = 4, initialFilters = {} } = {}) {
  const urlFilters = readFiltersFromUrl();
  const merged = { ...initialFilters, ...urlFilters };

  const filters = reactive({
    keyword:       merged.keyword       ?? '',
    type:          merged.type          ?? null,
    category_id:   merged.category_id   ?? null,
    zone_id:       merged.zone_id       ?? null,
    price_min:     merged.price_min     ?? null,
    price_max:     merged.price_max     ?? null,
    bedrooms_min:  merged.bedrooms_min  ?? null,
    bathrooms_min: merged.bathrooms_min ?? null,
    parking_min:   merged.parking_min   ?? null,
  });

  const bounds = ref(null);       // {north, south, east, west}
  const page = ref(1);
  const perPage = ref(initialPerPage);
  const mapPoints = ref([]);
  const mapPointsTruncated = ref(false);
  const properties = ref([]);
  const meta = ref({});
  const loading = ref(false);
  const error = ref(null);

  const priceRange = computed(() => ({
    min: meta.value.price_min ?? 0,
    max: meta.value.price_max ?? 1000000,
  }));

  // Sync filters to URL whenever they change
  const debouncedUrlSync = debounce(() => writeFiltersToUrl(filters), 300);
  watch(filters, debouncedUrlSync);
  onScopeDispose(() => debouncedUrlSync.cancel());

  async function search() {
    if (!bounds.value) return;

    loading.value = true;
    error.value = null;

    const params = {
      ...bounds.value,
      page: page.value,
      per_page: perPage.value,
    };

    // Only add non-null filter values
    if (filters.keyword) params.keyword = filters.keyword;
    if (filters.type) params.type = filters.type;
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.zone_id) params.zone_id = filters.zone_id;
    if (filters.price_min != null) params.price_min = filters.price_min;
    if (filters.price_max != null) params.price_max = filters.price_max;
    if (filters.bedrooms_min != null) params.bedrooms_min = filters.bedrooms_min;
    if (filters.bathrooms_min != null) params.bathrooms_min = filters.bathrooms_min;
    if (filters.parking_min != null) params.parking_min = filters.parking_min;

    try {
      const { data } = await client.get(searchUrl, { params });
      mapPoints.value = data.map_points || [];
      mapPointsTruncated.value = data.map_points_truncated || false;
      properties.value = data.properties || [];
      meta.value = data.meta || {};
    } catch (err) {
      if (err.response?.status === 422) {
        error.value = 'validation';
      } else if (err.response?.status === 429) {
        error.value = 'throttle';
      } else {
        error.value = 'network';
      }
      console.error('Property search error:', err);
    } finally {
      loading.value = false;
    }
  }

  const debouncedSearch = debounce(() => search(), 300);
  const debouncedFilterSearch = debounce(() => { page.value = 1; search(); }, 400);

  function setBounds(newBounds) {
    bounds.value = newBounds;
    page.value = 1;
    debouncedSearch();
  }

  function setFilters(newFilters) {
    Object.assign(filters, newFilters);
    debouncedFilterSearch();
  }

  function setPage(p) {
    page.value = p;
    search();
  }

  function setPerPage(pp) {
    perPage.value = pp;
    page.value = 1;
    search();
  }

  async function fetchProperty(id) {
    try {
      const baseUrl = searchUrl.replace('/search', '');
      const { data } = await client.get(`${baseUrl}/${id}`);
      return data;
    } catch {
      return null;
    }
  }

  function resetFilters() {
    filters.keyword = '';
    filters.type = null;
    filters.category_id = null;
    filters.zone_id = null;
    filters.price_min = null;
    filters.price_max = null;
    filters.bedrooms_min = null;
    filters.bathrooms_min = null;
    filters.parking_min = null;
    page.value = 1;
    search();
  }

  return {
    filters,
    bounds,
    page,
    perPage,
    mapPoints,
    mapPointsTruncated,
    properties,
    meta,
    loading,
    error,
    priceRange,
    setBounds,
    setFilters,
    setPage,
    setPerPage,
    resetFilters,
    search,
    fetchProperty,
  };
}
